import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import RevolutCheckout from '@revolut/checkout';
import Header from '../view/layout/Header';
import Footer from '../view/layout/Footer';
import { REVOLUT_MODE, REVOLUT_PUBLIC_TOKEN } from '../model/Config';
import { GetInvoiceDetails, StartInvoicePayment, ReconcileInvoice } from '../model/IndexModel';

/*
  Paying a monthly invoice.

  The rule this whole component is built around: **a bank transfer returning is not a payment.**
  When a hirer approves a Pay by Bank transfer, their bank has accepted the instruction — the
  money has not necessarily moved. Only the Revolut webhook confirms settlement, and only the
  server acts on it. So nothing here ever sets "Paid" on its own; it shows "Awaiting payment
  confirmation" and asks the server, which re-reads the live state from Revolut.

  This is the ONLY place in the guest app that calls payByBank(). Pay by Bank settles an invoice;
  it is never a way to pay for a booking, and the checkout modal (RevolutPaymentModal) neither
  imports nor references it. A test asserts that there is exactly one call site.
*/

const POLL_MS = 4000;
const POLL_GIVE_UP_MS = 3 * 60 * 1000;

const money = (n, ccy) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: ccy || 'GBP' }).format(Number(n) || 0);

const prettyDate = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).length <= 10 ? iso + 'T12:00:00Z' : iso);
  return Number.isNaN(d.getTime()) ? String(iso)
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const InvoicePayment = () => {
  const { invoice_token: invoiceToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  // { facilities_token: 'Hall name' }, sent alongside the invoice. The lines themselves carry
  // only a token, and a breakdown that cannot name the hall is not worth showing.
  const [hallNames, setHallNames] = useState({});
  // Only for invoices raised before the reference was stored on the line; empty for new ones.
  const [bookingRefs, setBookingRefs] = useState({});
  const [showLines, setShowLines] = useState(false);
  const [error, setError] = useState('');
  const [needsLogin, setNeedsLogin] = useState(false);
  // Header renders the account menu and the sign-in modal from this, exactly as every other page
  // does (see controller/CommonController.js).
  //
  // Starts as {} and never null. Header dereferences userSession.customer_token in a useEffect
  // dependency array, which React evaluates during render, so a null here takes the whole page
  // down before it draws. The other pages survive only because they start this as `null || []`,
  // which is [] — load-bearing by accident. {} says the same thing on purpose.
  const [userSession, setUserSession] = useState({});
  // idle | starting | awaiting_bank | awaiting_confirmation | paid | failed | cancelled
  const [stage, setStage] = useState('idle');
  const [notice, setNotice] = useState('');
  const [order, setOrder] = useState(null);

  const pollTimer = useRef(null);
  const pollStarted = useRef(0);
  const widgetRef = useRef(null);     // payByBank instance, or the legacy card instance
  const paymentsRef = useRef(null);   // the payments module itself

  const destroyWidgets = useCallback(() => {
    try { widgetRef.current?.destroy?.(); } catch (e) { /* already gone */ }
    try { paymentsRef.current?.destroy?.(); } catch (e) { /* already gone */ }
    widgetRef.current = null;
    paymentsRef.current = null;
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);

    // Check for a session BEFORE calling the API, not after. A 401 does not simply return an
    // error here: the axios interceptor clears storage and hard-redirects to '/' (see
    // model/apis.js handleAxiosError). So an unauthenticated visit would bounce the hirer to the
    // homepage with no explanation and no idea why. Asking first avoids that entirely.
    let token = null;
    try {
      token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch (e) { /* storage blocked — treat as signed out */ }

    if (!token) {
      setNeedsLogin(true);
      setLoading(false);
      return;
    }

    setNeedsLogin(false);
    const res = await GetInvoiceDetails(invoiceToken);
    if (res?.error || !res?.invoice) {
      setError(res?.error || 'This invoice could not be found.');
    } else {
      setInvoice(res.invoice);
      setHallNames(res.hall_names || {});
      setBookingRefs(res.booking_refs || {});
      if (res.invoice.state === 'paid') setStage('paid');
    }
    setLoading(false);
  }, [invoiceToken]);

  /* Open the site's sign-in box.
     The Header owns it, and already opens it on seeing location.state.openLogin — the signal the
     "Book Now while logged out" flow uses (view/layout/Header.js). Reusing that keeps the modal's
     ownership in one place instead of reaching across components. A fresh state object each time
     means the Header's effect re-fires if the hirer closes the box and clicks again.

     openLogin and NOTHING ELSE. In particular, do not add `from`: after a successful sign-in the
     Header picks one of three paths, and `from` selects the middle one —

       if (pendingBooking)            -> /checkout
       else if (state.from)           -> navigate(from)                    <- no reload
       else                           -> navigate(here) + location.reload()

     A client-side navigate to the route you are already on does not remount this component, so
     it never re-reads the session and the hirer sits looking at the same signed-out page. Only
     the last branch reloads, which is the one that actually shows them their invoice. */
  const openSignIn = useCallback(() => {
    navigate(location.pathname, { replace: true, state: { openLogin: true } });
  }, [navigate, location.pathname]);

  useEffect(() => {
    try {
      // BOTH stores, in the same order as the authToken read above, because sign-in writes them
      // as a pair: with cookie consent accepted the token AND the user record go to
      // localStorage (Header.js:345-359, IndexModel.js:142); without it both stay in
      // sessionStorage. Reading the token from both but the user from one meant that a hirer
      // arriving in a NEW TAB from the invoice email — sessionStorage is per tab, localStorage
      // is not — was judged signed in and then had no user record to show for it.
      const stored = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (stored) setUserSession(JSON.parse(stored) || {});
    } catch (e) { /* storage blocked or malformed — the {} default stands */ }
    load();
  }, [load]);
  useEffect(() => () => { stopPolling(); destroyWidgets(); }, [stopPolling, destroyWidgets]);

  /* Ask the server whether the webhook has confirmed settlement yet. The server is the only
     thing that can answer that; this just reports what it says. */
  const checkOnce = useCallback(async () => {
    const res = await ReconcileInvoice(invoiceToken);
    if (res?.paid) {
      stopPolling();
      destroyWidgets();
      setStage('paid');
      setNotice('');
      load();
      return true;
    }
    if (Date.now() - pollStarted.current > POLL_GIVE_UP_MS) {
      stopPolling();
      setNotice(
        'We have not had confirmation from your bank yet. This can take a little longer than ' +
        'usual — your payment is not lost. Refresh this page in a few minutes, or come back later.'
      );
    }
    return false;
  }, [invoiceToken, load, stopPolling, destroyWidgets]);

  const beginPolling = useCallback(() => {
    stopPolling();
    pollStarted.current = Date.now();
    setStage('awaiting_confirmation');
    checkOnce();
    pollTimer.current = setInterval(checkOnce, POLL_MS);
  }, [checkOnce, stopPolling]);

  /* ── The real Revolut widgets ────────────────────────────────────────────────
     Two surfaces, one rule: whichever one the hirer uses, success means the
     instruction was accepted, so both hand off to beginPolling() and let the
     server decide when the invoice is actually paid. */
  const launchRealWidget = useCallback(async (publicId, method) => {
    if (!REVOLUT_PUBLIC_TOKEN) {
      setNotice('Online payment is not configured for this environment.');
      setStage('idle');
      return;
    }
    try {
      if (method === 'card') {
        // The legacy surface, used here only as the quieter alternative to a bank transfer.
        // savePaymentMethodFor is deliberately NOT passed: a monthly invoice never saves a card.
        // The booking checkout sets it unconditionally, which is exactly why that flow could not
        // be reused for invoices.
        const inst = await RevolutCheckout(publicId, REVOLUT_MODE);
        widgetRef.current = inst;
        inst.payWithPopup({
          onSuccess: () => beginPolling(),
          onError: () => setStage('failed'),
          onCancel: () => setStage('cancelled'),
        });
        return;
      }

      // mode MUST be passed. The SDK types it optional and defaults it to 'prod', so omitting it
      // — as Revolut's own published example does — would point a sandbox build at live Revolut.
      // locale is typed required.
      const payments = await RevolutCheckout.payments({
        publicToken: REVOLUT_PUBLIC_TOKEN,
        locale: 'en',
        mode: REVOLUT_MODE,
      });
      paymentsRef.current = payments;

      const instance = payments.payByBank({
        // The order already exists — the server created or reused it before we got here, which is
        // what stops a second live order ever being minted for one invoice.
        createOrder: async () => ({ publicId }),
        location: 'GB',
        // Authorised in the banking app. NOT settled — see the note at the top of this file.
        onSuccess: () => beginPolling(),
        onError: () => setStage('failed'),
        onCancel: () => setStage('cancelled'),
      });
      widgetRef.current = instance;
      instance.show();
    } catch (e) {
      console.error('Pay by Bank widget failed to start:', e);
      setNotice('We could not open the payment window. Please try again.');
      setStage('idle');
    }
  }, [beginPolling]);

  const startPayment = useCallback(async (method) => {
    setNotice('');
    setStage('starting');
    destroyWidgets();
    const res = await StartInvoicePayment(invoiceToken, method);

    if (res?.error) { setError(res.error); setStage('idle'); return; }
    if (res?.alreadyPaid) { setStage('paid'); load(); return; }

    setOrder(res);
    // The server tells us which path we are on. Deliberately NOT decided from a frontend
    // environment variable: a mis-set build must never be able to show a fake payment against
    // real money, so only the server's answer can put us on the simulated path.
    if (res.simulated) setStage('awaiting_bank');
    else await launchRealWidget(res.publicId, method);
  }, [invoiceToken, load, launchRealWidget, destroyWidgets]);

  /* The simulated bank. Stands in for the hirer's banking app so the whole lifecycle can be
     rehearsed — Pay by Bank has no Revolut sandbox. Only ever reachable when the server says
     simulated: true, which it can only say outside production. */
  const simulate = useCallback(async (outcome) => {
    setNotice('');
    if (outcome === 'cancel') { setStage('cancelled'); return; }

    const APIS_URL = process.env.REACT_APP_API_URL || '';
    try {
      await fetch(APIS_URL.replace(/\/+$/, '') + '/postapis/dev/simulate-invoice-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_token: invoiceToken, state: outcome }),
      });
    } catch (e) {
      setNotice('Could not reach the simulator: ' + e.message);
      return;
    }

    if (outcome === 'failed') { setStage('failed'); return; }
    beginPolling();
  }, [invoiceToken, beginPolling]);

  /* ── Render ──────────────────────────────────────────────────────────────────────────────
     Bootstrap 5 is loaded site-wide (public/index.html), and the brand red #eb2a25 is the
     primary action colour everywhere else on the site, so this page uses both rather than the
     standalone blue it started with. Every state below shares one frame — same card, same
     heading rhythm, same place for the status chip — because a page that rearranges itself
     between "due" and "paid" reads as two different pages.

     The one deliberate exception is the simulated bank panel, which stays dark and unbranded.
     It stands in for the hirer's banking app and must never look like part of Simply Halls. */

  // Every state is wrapped in the site's own header and footer, as every other page is.
  // The header is not decoration here: sign-in is a modal inside it, and there is no /login
  // route, so without it a signed-out hirer would have no way to sign in from this page at all.
  const chrome = (inner) => (
    <>
      <style>{SCOPED_CSS}</style>
      <Header userSession={userSession} />
      <main className="inv-page">
        <div className="container">
          <div className="inv-shell mx-auto">{inner}</div>
        </div>
      </main>
      <Footer />
    </>
  );

  // One card frame for every state, so nothing jumps between them.
  const card = (title, chip, body, sub) => (
    <div className="card inv-card">
      <div className="card-body p-4 p-md-4">
        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
          <div className="min-w-0">
            <div className="inv-eyebrow">Monthly invoice</div>
            <h1 className="inv-title">{title}</h1>
            {sub && <p className="inv-sub mb-0">{sub}</p>}
          </div>
          {chip}
        </div>
        {body}
      </div>
    </div>
  );

  if (loading) {
    return chrome(card(
      'Loading your invoice',
      null,
      <div className="d-flex align-items-center gap-2 inv-muted">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        <span>One moment.</span>
      </div>,
    ));
  }

  if (needsLogin) {
    return chrome(card(
      'Please sign in to view this invoice',
      null,
      <>
        <p className="inv-muted">
          Invoices are private to the hirer they were issued to, so you’ll need to sign in with
          the account the invoice was sent to.
        </p>
        {/* Opens the site's sign-in box. This used to reload the page, which simply redrew the
            same signed-out screen — leaving no way to sign in from the one page a hirer reaches
            by clicking a link in an email. */}
        <button type="button" className="btn inv-btn w-100 mt-2" onClick={openSignIn}>
          Sign in to view this invoice
        </button>
      </>,
    ));
  }

  if (error) {
    return chrome(card(
      'We couldn’t open this invoice',
      null,
      <>
        <p className="inv-muted">{error}</p>
        <p className="inv-muted mb-0">
          If you have more than one Simply Halls account, check you’re signed in as the one the
          invoice was sent to.
        </p>
      </>,
    ));
  }

  if (!invoice) return null;

  const ccy = invoice.currency || 'GBP';
  const paid = stage === 'paid' || invoice.state === 'paid';
  const overdue = !paid && invoice.due_date && new Date(invoice.due_date) < new Date();
  const lines = invoice.lines || [];
  const hallCount = new Set(lines.map((l) => l.facilities_token)).size;
  const hallOf = (l) => hallNames[l.facilities_token] || 'Hall';
  // Stored on the line from 2026-10-29; bookingRefs fills in invoices raised before that. The
  // em dash is for the rare record with no reference at all — a blank cell reads as a bug.
  const refOf = (l) => l.booking_ref || bookingRefs[l.order_token] || '—';

  const chipClass = paid ? 'inv-chip-ok' : overdue ? 'inv-chip-bad' : 'inv-chip-due';
  const chip = (
    <span className={'inv-chip ' + chipClass}>{paid ? 'Paid' : overdue ? 'Overdue' : 'Due'}</span>
  );

  const canPay = !paid && (stage === 'idle' || stage === 'failed' || stage === 'cancelled');

  return chrome(card(
    invoice.invoice_number,
    chip,
    <>
      <dl className="inv-facts">
        <dt>Period</dt><dd>{invoice.period}</dd>
        <dt>Issued</dt><dd>{prettyDate(invoice.issue_date)}</dd>
        <dt>Due</dt><dd>{prettyDate(invoice.due_date)}</dd>
        {paid && invoice.paid_at && (<><dt>Paid</dt><dd>{prettyDate(invoice.paid_at)}</dd></>)}
      </dl>

      <table className="inv-sums">
        <tbody>
          <tr>
            <td>
              Hire charges
              {/* The sessions behind the figure. Without this the hirer is asked to pay a
                  number they have no way to check — the breakdown was in the email only. */}
              {lines.length > 0 && (
                <button
                  type="button"
                  className="inv-link d-block mt-1"
                  aria-expanded={showLines}
                  onClick={() => setShowLines((v) => !v)}
                >
                  {showLines ? 'Hide' : 'Show'} {lines.length}{' '}
                  {lines.length === 1 ? 'session' : 'sessions'}
                  {hallCount > 1 ? ` at ${hallCount} halls` : ''}
                </button>
              )}
            </td>
            <td className="inv-num">{money(invoice.hire_total, ccy)}</td>
          </tr>

          {showLines && lines.map((l, i) => (
            <tr key={l.order_token || i} className="inv-line">
              <td>
                {/* The reference on the hirer's own booking. Without it two sessions on the
                    same day at the same hall look identical on this list. */}
                <span className="inv-line-ref">{refOf(l)}</span>
                <span className="inv-line-date">{prettyDate(l.date)}</span>
                {hallCount > 1 && <span className="inv-line-hall">{hallOf(l)}</span>}
                {l.cancelled && <span className="inv-line-flag">Cancelled</span>}
                {/* This session's equal share of the booking fee at the foot. Shown from
                    2 Nov 2026: the hirer could previously see only the one combined figure and
                    not which session carried what. Kept in this cell rather than given a column
                    of its own, because every other row in this table is two columns and the
                    shares are a detail of the session, not a second amount being charged.
                    Absent on invoices raised before the split, which carry no per-line fee. */}
                {Number(l.booking_fee) > 0 && (
                  <span className="inv-line-fee">+ {money(l.booking_fee, ccy)} fee</span>
                )}
              </td>
              <td className="inv-num">{money(l.net, ccy)}</td>
            </tr>
          ))}

          <tr>
            {/* The "(3%)" that used to be here was hard-coded and is now wrong whenever the
                £2.25 minimum decides the fee instead of the percentage — a £30 month pays £2.25,
                which is 7.5%, and the label would have said 3% beside it. The page cannot tell
                the two cases apart (it holds hire_total, the sum of NET, not the gross the
                percentage is taken on), so the claim is dropped rather than guessed at. The
                figure itself is what the hirer is being asked to pay. */}
            <td>Booking fee</td>
            <td className="inv-num">{money(invoice.booking_fee, ccy)}</td>
          </tr>
          {Number(invoice.damage_total) > 0 && (
            <>
              <tr>
                <td>Damages</td>
                <td className="inv-num">{money(invoice.damage_total, ccy)}</td>
              </tr>
              <tr>
                <td>Damage fee</td>
                <td className="inv-num">{money(invoice.damage_fee, ccy)}</td>
              </tr>
            </>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td>Total {paid ? 'paid' : 'due'}</td>
            <td className="inv-num">{money(invoice.invoice_total, ccy)}</td>
          </tr>
        </tfoot>
      </table>

      {paid && (
        <div className="inv-banner inv-banner-ok">
          <strong>Paid in full.</strong> This invoice is your receipt — there is nothing else to do.
        </div>
      )}

      {stage === 'awaiting_confirmation' && (
        <div className="inv-banner inv-banner-info">
          <strong>Awaiting payment confirmation.</strong> Your bank has accepted the instruction.
          We will mark this invoice paid as soon as the payment clears, which is usually within a
          minute. You can safely leave this page.
        </div>
      )}

      {stage === 'failed' && (
        <div className="inv-banner inv-banner-bad">
          <strong>That payment didn’t go through.</strong> Nothing has been taken. You can try
          again below.
        </div>
      )}

      {stage === 'cancelled' && (
        <div className="inv-banner inv-banner-warn">
          You closed the payment without approving it. Nothing has been taken.
        </div>
      )}

      {notice && <div className="inv-banner inv-banner-warn">{notice}</div>}

      {/* The simulated bank — only when the SERVER says this is a simulated order. Kept dark and
          off-brand on purpose: it stands in for the hirer's banking app. */}
      {stage === 'awaiting_bank' && order?.simulated && (
        <div className="inv-bank">
          <div className="inv-bank-head">Simulated bank — not a real payment</div>
          <div className="inv-bank-amount">Approve {money(invoice.invoice_total, ccy)}?</div>
          <div className="inv-bank-sub">
            To Simply Halls · reference {invoice.invoice_number}
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button type="button" className="btn inv-bank-btn" onClick={() => simulate('completed')}>Approve</button>
            <button type="button" className="btn inv-bank-ghost" onClick={() => simulate('failed')}>Decline</button>
            <button type="button" className="btn inv-bank-ghost" onClick={() => simulate('cancel')}>Close without paying</button>
          </div>
        </div>
      )}

      {/* Pay by Bank first and prominent; card a quiet alternative below it. */}
      {canPay && (
        <>
          <button type="button" className="btn inv-btn w-100" onClick={() => startPayment('pay_by_bank')}>
            Pay by bank
          </button>
          <p className="inv-hint">Approve securely in your banking app — no card details needed.</p>
          <p className="text-center mb-0 mt-3">
            <button type="button" className="inv-link" onClick={() => startPayment('card')}>
              Can’t pay from your bank account? Pay by card instead.
            </button>
          </p>
        </>
      )}

      {stage === 'starting' && (
        <p className="inv-muted d-flex align-items-center gap-2 mb-0">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          Setting up your payment…
        </p>
      )}
    </>,
    paid ? 'Thank you — this is your receipt.' : null,
  ));
};

/* Scoped to .inv-* so nothing here can reach the rest of the site. Brand red #eb2a25 matches
   index.css and the buttons used across the app; the greys are the same family the site's cards
   already use. The app has no dark mode, so there is one palette. */
const SCOPED_CSS = `
.inv-page { background: #f6f7fa; padding: 2.5rem 0 3.5rem; }
.inv-shell { max-width: 620px; }
.inv-card { border: 1px solid #e3e7ee; border-radius: 12px; box-shadow: 0 1px 3px rgba(22,27,36,.05); }
.inv-eyebrow { font-size: .6875rem; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: #8a919e; }
.inv-title { font-size: 1.5rem; font-weight: 700; margin: .25rem 0 0; color: #161b24; overflow-wrap: anywhere; }
.inv-sub { font-size: .875rem; color: #6b7280; margin-top: .35rem; }
.inv-muted { color: #6b7280; font-size: .875rem; }
.min-w-0 { min-width: 0; }

.inv-chip { flex: none; font-size: .6875rem; font-weight: 700; letter-spacing: .06em;
  text-transform: uppercase; padding: .3rem .6rem; border-radius: 4px; white-space: nowrap; }
.inv-chip-due { background: #fdf0e0; color: #96580f; }
.inv-chip-ok  { background: #e4f1ea; color: #27694a; }
.inv-chip-bad { background: #fbe9e8; color: #a3302c; }

.inv-facts { display: grid; grid-template-columns: auto 1fr; gap: .4rem 1.25rem; margin: 0 0 1.25rem; }
.inv-facts dt { font-size: .6875rem; font-weight: 700; letter-spacing: .07em; text-transform: uppercase; color: #8a919e; padding-top: .15rem; }
.inv-facts dd { margin: 0; font-size: .9375rem; color: #161b24; }

.inv-sums { width: 100%; border-collapse: collapse; font-size: .9375rem; margin-bottom: 1.25rem; }
.inv-sums td { padding: .6rem 0; border-bottom: 1px solid #eef1f6; vertical-align: top; color: #161b24; }
.inv-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; padding-left: 1rem !important; }
.inv-sums tfoot td { padding: .7rem 0 0; border-bottom: 0; border-top: 2px solid #161b24; font-weight: 700; font-size: 1rem; }

.inv-line td { padding: .3rem 0; border-bottom: 1px dashed #eef1f6; font-size: .8125rem; color: #6b7280; }
.inv-line-ref { display: inline-block; min-width: 4.2em; margin-right: .5rem; color: #161b24;
  font-family: ui-monospace, Consolas, "Courier New", monospace; font-size: .78rem; }
.inv-line-date { display: inline-block; }
.inv-line-hall { display: inline-block; margin-left: .5rem; color: #8a919e; }
.inv-line-fee { display: inline-block; margin-left: .5rem; color: #8a919e; font-variant-numeric: tabular-nums; }
.inv-line-flag { display: inline-block; margin-left: .5rem; font-size: .6875rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: .05em; color: #a3302c; }

.inv-banner { padding: .8rem .95rem; border-radius: 7px; font-size: .875rem; line-height: 1.55; margin-bottom: .9rem; }
.inv-banner-ok   { background: #e4f1ea; color: #1d4a33; }
.inv-banner-info { background: #e7eff6; color: #1d5a84; }
.inv-banner-bad  { background: #fbe9e8; color: #a3302c; }
.inv-banner-warn { background: #fdf0e0; color: #8a5410; }

.inv-btn { background: #eb2a25; border: 0; color: #fff; border-radius: 8px; padding: .85rem 1rem;
  font-size: 1rem; font-weight: 700; }
.inv-btn:hover, .inv-btn:focus { background: #cf211c; color: #fff; }
.inv-hint { text-align: center; color: #8a919e; font-size: .8125rem; margin: .6rem 0 0; }
.inv-link { background: none; border: 0; padding: 0; color: #eb2a25; text-decoration: underline;
  font-size: .8125rem; cursor: pointer; }
.inv-link:hover { color: #cf211c; }

.inv-bank { background: #161b24; color: #f6f7fa; border-radius: 10px; padding: 1.25rem; margin-bottom: 1rem; }
.inv-bank-head { font-size: .6875rem; font-weight: 700; letter-spacing: .11em; text-transform: uppercase; opacity: .65; margin-bottom: .6rem; }
.inv-bank-amount { font-size: 1.125rem; font-weight: 600; margin-bottom: .2rem; }
.inv-bank-sub { font-size: .8125rem; opacity: .75; margin-bottom: .9rem; overflow-wrap: anywhere; }
.inv-bank-btn { background: #1f5f8b; border: 0; color: #fff; border-radius: 6px; padding: .55rem 1rem; font-weight: 600; font-size: .875rem; }
.inv-bank-btn:hover { background: #1a5076; color: #fff; }
.inv-bank-ghost { background: transparent; border: 1px solid rgba(255,255,255,.35); color: #f6f7fa; border-radius: 6px; padding: .55rem 1rem; font-weight: 600; font-size: .875rem; }
.inv-bank-ghost:hover { background: rgba(255,255,255,.1); color: #fff; }

@media (max-width: 575.98px) {
  .inv-page { padding: 1.5rem 0 2.5rem; }
  .inv-title { font-size: 1.25rem; }
  .inv-card .card-body { padding: 1.25rem !important; }
}
`;

export default InvoicePayment;
